// src/pages/Home/LastVieClubs.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAllArticles } from "../../services/vieclubServices";
import Button from "../../components/Button/Button";
import { useUser } from "../../contexts/UserContext";
import { FaArrowRight, FaTimes } from "react-icons/fa";

const ARTICLES_PER_PAGE = 3;

const LastVieClubs = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [zoomImage, setZoomImage] = useState(false);

  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const fetchedArticles = await getAllArticles();
        // On laisse tout, la pagination gère l’affichage (3 par page)
        setArticles(fetchedArticles || []);
      } catch (error) {
        console.error("Failed to fetch articles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(articles.length / ARTICLES_PER_PAGE)),
    [articles.length]
  );

  const articlesToDisplay = useMemo(() => {
    if (!articles.length) return [];
    const start = page * ARTICLES_PER_PAGE;
    return articles.slice(start, start + ARTICLES_PER_PAGE);
  }, [articles, page]);

  const handleViewMore = () => {
    navigate("/vie-des-clubs");
  };

  const goNextPage = () => {
    setPage((prev) => (prev + 1) % pageCount);
  };

  const goPrevPage = () => {
    setPage((prev) => (prev - 1 + pageCount) % pageCount);
  };

  const formatArticleDate = (a) => {
    const raw =
      a.date_article || a.date_publication || a.createdAt || a.created_at;
    if (!raw) return null;
    return new Date(raw).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getArticleImage = (a) => {
    return (
      a.photo_url1 || // nouvelle structure VieClub
      a.photo_url2 ||
      a.photo_url3 ||
      a.photo_url || // compat ancien format
      a.image_url || // autre éventuel champ
      "/img/default_image.jpg" // fallback
    );
  };

  const getArticleTitle = (a) => {
    return a.titre || a.title || "Article de club";
  };

  const getArticleContent = (a) => {
    return a.contenu || a.content || a.description || "";
  };

  const getArticleClub = (a) => {
    return a.club_nom || a.club || a.auteur || a.club_name || null;
  };

  const openModal = (article) => {
    setSelectedArticle(article);
    setZoomImage(false);
  };

  const closeModal = () => {
    setSelectedArticle(null);
    setZoomImage(false);
  };

  // Bloquer les liens dans l’aperçu
  const handlePreviewContentClick = (e) => {
    const target = e.target;
    if (target && target.closest("a")) {
      e.preventDefault();
    }
  };

  /* ========== Loading ========== */
  if (loading) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-6 md:mb-8 animate-pulse">
            <div className="h-6 w-80 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
            <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 h-64"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ========== Pas d’articles ========== */
  if (!articles.length) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
            Vie des clubs
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">
            Derniers articles de la vie des clubs
          </h2>
          <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
            Aucun article n’a encore été publié par les clubs.
          </p>
        </div>
      </section>
    );
  }

  /* ========== Affichage normal ========== */
  return (
    <>
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Titre */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6 md:mb-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                Vie des clubs
              </p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                Derniers articles de la vie des clubs
              </h2>
            </div>
            <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 max-w-md">
              Mise en avant des projets, événements et réussites des clubs
              girondins.
            </p>
          </div>

          {/* Cartes : 3 par page */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {articlesToDisplay.map((a) => {
              const title = getArticleTitle(a);
              const content = getArticleContent(a);
              const image = getArticleImage(a);
              const club = getArticleClub(a);
              const dateStr = formatArticleDate(a);

              return (
                <article
                  key={a.id}
                  onClick={() => openModal(a)}
                  className="group flex flex-col bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={image}
                      alt={title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent pointer-events-none" />
                    {club && (
                      <div className="absolute left-3 bottom-3 bg-white/95 rounded-full px-3 py-1 text-[11px] font-semibold text-slate-900 shadow-md">
                        {club}
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="flex flex-col flex-grow p-4 space-y-3">
                    <h3 className="text-base md:text-lg font-semibold text-slate-900 dark:text-slate-50 line-clamp-2">
                      {title}
                    </h3>

                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-700 dark:text-slate-300">
                      {dateStr && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-700/70">
                          Publié le {dateStr}
                        </span>
                      )}
                    </div>

                    {content && (
                      <div
                        className="text-xs md:text-sm text-slate-700 dark:text-slate-300 line-clamp-5"
                        onClick={handlePreviewContentClick}
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {/* Pagination + CTA */}
          <div className="mt-6 md:mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Pagination pages de 3 */}
            {pageCount > 1 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={goPrevPage}
                  className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  ◀ Précédents
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: pageCount }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setPage(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === page
                          ? "bg-primary"
                          : "bg-slate-300 dark:bg-slate-600"
                      }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={goNextPage}
                  className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Suivants ▶
                </button>
              </div>
            )}

            {/* Bouton voir plus (réservé aux users connectés comme avant) */}
            {user && (
              <button
                type="button"
                onClick={handleViewMore}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary text-primary text-xs md:text-sm font-semibold uppercase tracking-[0.18em] bg-white dark:bg-slate-900 hover:bg-primary hover:text-white transition-colors"
              >
                <span>Voir plus d'articles</span>
                <FaArrowRight className="text-sm" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* MODALE ARTICLE VIE DES CLUBS */}
      {selectedArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4">
          {/* fond sombre */}
          <div
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* contenu modale */}
          <div
            className="relative max-w-3xl w-full max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* bouton fermer */}
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-900/80 text-slate-100 hover:bg-slate-800"
              aria-label="Fermer"
            >
              <FaTimes className="w-4 h-4" />
            </button>

            {/* Image + zoom */}
            <div
              className={`relative bg-black ${
                zoomImage ? "max-h-[70vh]" : "max-h-72 sm:max-h-80"
              } overflow-hidden flex items-center justify-center ${
                zoomImage ? "cursor-zoom-out" : "cursor-zoom-in"
              }`}
              onClick={() => setZoomImage((z) => !z)}
            >
              <img
                src={getArticleImage(selectedArticle)}
                alt={getArticleTitle(selectedArticle)}
                className={`w-full h-full transition-all duration-300 ${
                  zoomImage ? "object-contain" : "object-cover"
                }`}
              />
              <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[11px] px-3 py-1 rounded-full">
                Cliquer pour {zoomImage ? "réduire" : "agrandir"}
              </div>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="mb-3">
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                  Vie des clubs
                </p>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
                  {getArticleTitle(selectedArticle)}
                </h3>
              </div>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-2 mb-4 text-xs sm:text-[13px] text-slate-600 dark:text-slate-300">
                {getArticleClub(selectedArticle) && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800/80">
                    {getArticleClub(selectedArticle)}
                  </span>
                )}
                {formatArticleDate(selectedArticle) && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-50 dark:bg-slate-800/80">
                    Publié le {formatArticleDate(selectedArticle)}
                  </span>
                )}
              </div>

              {/* Contenu complet (liens autorisés ici) */}
              {getArticleContent(selectedArticle) && (
                <div
                  className="prose prose-sm sm:prose-base max-w-none prose-headings:mt-4 prose-a:text-primary dark:prose-invert"
                  dangerouslySetInnerHTML={{
                    __html: getArticleContent(selectedArticle),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LastVieClubs;
